import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  getEventSpecificTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  toggleTestimonialFeatured
} from '../../services/landingPageService';

const { FiPlus, FiEdit2, FiTrash2, FiStar, FiX, FiUser, FiMessageSquare } = FiIcons;

export default function TestimonialsTab({ eventId }) {
  const [testimonials, setTestimonials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTestimonials();
  }, [eventId]);

  async function loadTestimonials() {
    try {
      setLoading(true);
      const { data, error } = await getEventSpecificTestimonials(eventId);
      if (error) throw error;
      setTestimonials(data || []);
    } catch (error) {
      console.error('[TestimonialsTab] Error loading testimonials:', error);
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(testimonial) {
    setEditingTestimonial(testimonial);
    setShowModal(true);
  }

  function handleAdd() {
    setEditingTestimonial(null);
    setShowModal(true);
  }

  async function handleDelete(testimonialId) {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      const { error } = await deleteTestimonial(testimonialId);
      if (error) throw error;

      await loadTestimonials();
      toast.success('Testimonial deleted');
    } catch (error) {
      console.error('[TestimonialsTab] Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  }

  async function handleToggleFeatured(testimonialId, currentFeatured) {
    try {
      const { error } = await toggleTestimonialFeatured(testimonialId, !currentFeatured);
      if (error) throw error;

      await loadTestimonials();
      toast.success(currentFeatured ? 'Testimonial unfeatured' : 'Testimonial featured');
    } catch (error) {
      console.error('[TestimonialsTab] Error toggling featured:', error);
      toast.error('Failed to update testimonial');
    }
  }

  async function handleSave(testimonialData) {
    try {
      if (editingTestimonial) {
        const { error } = await updateTestimonial(editingTestimonial.id, testimonialData);
        if (error) throw error;
        toast.success('Testimonial updated');
      } else {
        const maxOrder = testimonials.reduce((max, t) => Math.max(max, t.display_order || 0), 0);
        const { error } = await createTestimonial({
          ...testimonialData,
          event_id: eventId,
          display_order: maxOrder + 1
        });
        if (error) throw error;
        toast.success('Testimonial created');
      }

      setShowModal(false);
      setEditingTestimonial(null);
      await loadTestimonials();
    } catch (error) {
      console.error('[TestimonialsTab] Error saving testimonial:', error);
      toast.error('Failed to save testimonial');
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Testimonials</h2>
          <p className="text-sm text-gray-500 mt-1">
            Add social proof from past attendees
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Add Testimonial
        </button>
      </div>

      {/* Testimonials List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {testimonials.length === 0 ? (
          <div className="md:col-span-2 text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <SafeIcon icon={FiMessageSquare} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No testimonials added yet</p>
            <button
              onClick={handleAdd}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Add your first testimonial
            </button>
          </div>
        ) : (
          testimonials.map((testimonial) => (
            <div key={testimonial.id} className="bg-white rounded-lg shadow-sm p-6 relative">
              {testimonial.featured && (
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <SafeIcon icon={FiStar} className="w-3 h-3 mr-1" />
                    Featured
                  </span>
                </div>
              )}

              <div className="flex items-start space-x-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {testimonial.author_image_url ? (
                    <img
                      src={testimonial.author_image_url}
                      alt={testimonial.author_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center mb-1">
                    {[...Array(5)].map((_, i) => (
                      <SafeIcon
                        key={i}
                        icon={FiStar}
                        className={`w-4 h-4 ${i < (testimonial.rating || 5) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>

                  <p className="text-gray-700 italic mb-3">"{testimonial.content}"</p>

                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.author_name}</p>
                    {testimonial.author_title && (
                      <p className="text-sm text-gray-500">{testimonial.author_title}</p>
                    )}
                    {testimonial.author_company && (
                      <p className="text-sm text-gray-500">{testimonial.author_company}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-4 pt-4 border-t flex items-center justify-end space-x-2">
                <button
                  onClick={() => handleToggleFeatured(testimonial.id, testimonial.featured)}
                  className={`text-sm px-3 py-1 rounded transition-colors ${
                    testimonial.featured
                      ? 'text-yellow-700 bg-yellow-50 hover:bg-yellow-100'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {testimonial.featured ? 'Unfeature' : 'Feature'}
                </button>
                <button
                  onClick={() => handleEdit(testimonial)}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Edit"
                >
                  <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(testimonial.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <TestimonialModal
          testimonial={editingTestimonial}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingTestimonial(null);
          }}
        />
      )}
    </div>
  );
}

function TestimonialModal({ testimonial, onSave, onClose }) {
  const [formData, setFormData] = useState({
    content: testimonial?.content || '',
    author_name: testimonial?.author_name || '',
    author_title: testimonial?.author_title || '',
    author_company: testimonial?.author_company || '',
    author_image_url: testimonial?.author_image_url || '',
    rating: testimonial?.rating || 5,
    featured: testimonial?.featured || false
  });
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.content.trim()) {
      toast.error('Testimonial content is required');
      return;
    }
    if (!formData.author_name.trim()) {
      toast.error('Author name is required');
      return;
    }

    setSaving(true);
    await onSave(formData);
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 my-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {testimonial ? 'Edit Testimonial' : 'Add Testimonial'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Testimonial Content *
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="What did they say about the event?"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Author Name *
              </label>
              <input
                type="text"
                value={formData.author_name}
                onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title/Role
              </label>
              <input
                type="text"
                value={formData.author_title}
                onChange={(e) => setFormData({ ...formData, author_title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Financial Advisor"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <input
              type="text"
              value={formData.author_company}
              onChange={(e) => setFormData({ ...formData, author_company: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Author Image URL
            </label>
            <input
              type="url"
              value={formData.author_image_url}
              onChange={(e) => setFormData({ ...formData, author_image_url: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://example.com/photo.jpg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rating
            </label>
            <div className="flex items-center space-x-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setFormData({ ...formData, rating: star })}
                  className="focus:outline-none"
                >
                  <SafeIcon
                    icon={FiStar}
                    className={`w-6 h-6 ${star <= formData.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                  />
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.featured}
              onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Featured testimonial</span>
          </label>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : testimonial ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
